/**
 * CommunityLayout Component
 * 커뮤니티 메인 레이아웃
 *
 * 리팩토링: 1,993줄 → 약 150줄
 * - 섹션 컴포넌트 분리: AnnouncementsSection, FreeBoardSection, IssueBoardSection 등
 * - 유틸리티 분리: formatDate, getToken
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import NewsBriefing from './NewsBriefing';
import News from './News';
import NewsInsight from './NewsInsight';
import Archive from './Archive';
import Profile from './Profile';
import {
  AnnouncementsSection,
  FreeBoardSection,
  IssueBoardSection,
  PostDetail,
} from './community';
import './CommunityLayout.css';


// Dev Hub → MarkdownBlog 리다이렉트
const RedirectToMarkdownBlog = () => {
  React.useEffect(() => {
    window.location.href = 'https://markdown.ilouli.com/project/ilouli-community';
  }, []);
  return <div style={{ padding: '40px', textAlign: 'center' }}>MarkdownBlog로 이동 중...</div>;
};

// 직접 링크로 게시글 접근하는 래퍼 컴포넌트
const PostDetailDirect = ({ backTo }) => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetch(`/api/community/posts/${postId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.post) {
          const p = data.data.post;
          const boardPath = p.board === 'announcement' ? '/announcements'
            : p.board === 'issue' ? '/issue-board'
            : '/free-board';
          setPost({
            id: p.id,
            type: p.board === 'announcement' ? 'announcement' : 'community',
            category: p.category || null,
            title: p.title,
            content: p.content,
            source_data: p.source_data || null,
            author: { id: p.author_id, name: p.author_name, email: p.author_email, picture: p.author_picture },
            createdAt: p.created_at,
            updatedAt: p.updated_at,
            comments: (data.data.comments || []).map(c => ({
              id: c.id,
              content: c.content,
              author: { id: c.author_id, name: c.author_name, email: c.author_email, picture: c.author_picture },
              createdAt: c.created_at,
              comment_type: c.comment_type || "comment",
              parent_id: c.parent_id,
            })),
            attachments: [],
            reportCount: 0,
            isHidden: false,
            _boardPath: backTo || boardPath,
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [postId, backTo]);

  if (loading) return <div className="section-container"><p>로딩 중...</p></div>;
  if (!post) return <div className="section-container"><p>게시글을 찾을 수 없습니다.</p></div>;
  return <PostDetail post={post} onBack={() => navigate(post._boardPath)} />;
};

const CommunityLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // 모바일 메뉴 토글
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  // 라우트 변경 시 모바일 메뉴 닫기
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  // 모바일 메뉴 열릴 때 배경 스크롤 방지
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // 현재 활성 섹션 결정
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/announcements')) return 'announcements';
    if (path.includes('/free-board')) return 'free-board';
    if (path.includes('/issue-board')) return 'issue-board';
    if (path.includes('/news/insight')) return 'news';
    if (path.includes('/news')) return 'news';
    if (path.includes('/briefing')) return 'briefing';
    return 'announcements';
  };

  const activeSection = getActiveSection();

  // 글 클릭 시 해당 게시글 URL로 이동
  const handlePostSelect = useCallback((post) => {
    const path = location.pathname;
    let board = 'free-board';
    if (path.includes('/announcements')) board = 'announcements';
    else if (path.includes('/issue-board')) board = 'issue-board';
    else if (path.includes('/free-board')) board = 'free-board';
    navigate(`/${board}/${post.id}`);
  }, [location.pathname, navigate]);

  // 공통 사이드바 props
  const sidebarProps = {
    collapsed: sidebarCollapsed,
    onToggle: () => setSidebarCollapsed(!sidebarCollapsed),
    selectedCategory,
    onCategorySelect: (cat) => {
      setSelectedCategory(cat);
      closeMobileMenu();
      // 항상 URL에 카테고리 반영 (이미 free-board에 있어도)
      navigate(cat ? `/free-board?category=${cat}` : '/free-board', { replace: location.pathname === '/free-board' });
    },
    activeSection,
    basePath: "",
    mobileOpen: mobileMenuOpen,
    onMobileClose: closeMobileMenu
  };

  // 공통 모바일 헤더
  const MobileHeader = () => (
    <div className="mobile-content-header">
      <button className="mobile-menu-toggle" onClick={toggleMobileMenu} aria-label="메뉴 열기">
        <span></span>
        <span></span>
        <span></span>
      </button>
      <span className="mobile-page-title">Community</span>
    </div>
  );

  return (
    <div className="test-page-layout">
      {mobileMenuOpen && <div className="mobile-sidebar-overlay" onClick={closeMobileMenu} />}
      <Sidebar {...sidebarProps} />
      <main className="test-page-content">
        <MobileHeader />
        <Routes>
          <Route path="/" element={<Navigate to="/announcements" replace />} />
          <Route path="/announcements" element={<AnnouncementsSection onPostSelect={handlePostSelect} />} />
          <Route path="/announcements/:postId" element={<PostDetailDirect backTo="/announcements" />} />
          <Route path="/free-board" element={<FreeBoardSection onPostSelect={handlePostSelect} selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} />} />
          <Route path="/free-board/post/:postId" element={<PostDetailDirect backTo="/free-board" />} />
          <Route path="/free-board/:postId" element={<PostDetailDirect backTo="/free-board" />} />
          <Route path="/issue-board" element={<IssueBoardSection onPostSelect={handlePostSelect} />} />
          <Route path="/issue-board/:postId" element={<PostDetailDirect backTo="/issue-board" />} />
          {/* Dev Hub removed - redirect to MarkdownBlog */}
          <Route path="/dev-hub/*" element={<RedirectToMarkdownBlog />} />
          <Route path="/dev-progress" element={<RedirectToMarkdownBlog />} />
          <Route path="/prd" element={<RedirectToMarkdownBlog />} />
          <Route path="/dev-complete" element={<RedirectToMarkdownBlog />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/insight" element={<NewsInsight />} />
          <Route path="/briefing" element={<NewsBriefing />} />
          <Route path="/news-archive" element={<Archive type="news" />} />
          <Route path="/briefing-archive" element={<Archive type="briefing" />} />
          <Route path="/youtube-archive" element={<Archive type="youtube" />} />
          <Route path="/profile" element={<Profile />} />
        </Routes>
      </main>
    </div>
  );
};

export default CommunityLayout;
